/**
 * « il y a 5 min », « hier », « il y a 2 sem. » — how long ago a page was
 * visited, in the page's locale (`format.locale` of the home translation).
 *
 * The words are the browser's own (`Intl.RelativeTimeFormat`), short style,
 * with `numeric: 'auto'` so that one day ago reads « hier ». Under a minute,
 * « maintenant ». Past four weeks the date itself is clearer than a count.
 */

const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;

/**
 * From this age on, in milliseconds, `relativeTime` gives the date itself
 * rather than a count — for a caller that shows a date already.
 */
export const RELATIVE_TIME_LIMIT_MS = 4 * WEEK * 1000;

export function relativeTime(at: number, now: number, locale: string): string {
  const seconds = Math.round((now - at) / 1000);
  const format = new Intl.RelativeTimeFormat(locale, {
    numeric: 'auto',
    style: 'short',
  });

  if (seconds < MINUTE) {
    return format.format(0, 'second');
  }
  if (seconds < HOUR) {
    return format.format(-Math.floor(seconds / MINUTE), 'minute');
  }
  if (seconds < DAY) {
    return format.format(-Math.floor(seconds / HOUR), 'hour');
  }
  if (seconds < WEEK) {
    return format.format(-Math.floor(seconds / DAY), 'day');
  }
  if (seconds * 1000 < RELATIVE_TIME_LIMIT_MS) {
    return format.format(-Math.floor(seconds / WEEK), 'week');
  }
  return new Date(at).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
